import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/SEOHead";
import { CheckCircle2, XCircle, Clock, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const AutomationLogs = () => {
  const { toast } = useToast();

  const { data: logs, isLoading, refetch } = useQuery({
    queryKey: ["automation-logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("automation_log")
        .select("*")
        .order("timestamp", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
  });

  const handleManualSync = async () => {
    toast({
      title: "Starting manual sync...",
      description: "This may take a few minutes.",
    });

    try {
      const { error } = await supabase.functions.invoke("auto-sync-ideas");
      if (error) throw error;

      toast({
        title: "Sync complete!",
        description: "Ideas have been synchronized successfully.",
      });

      refetch();
    } catch (error) {
      toast({
        title: "Sync failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Automation Logs - DayOne"
        description="View automated idea sync history and logs"
      />

      <div className="container max-w-6xl mx-auto px-4 py-16">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold">Automation Logs</h1>
            <p className="text-muted-foreground mt-2">
              Track automated idea syncing and system health
            </p>
          </div>
          <Button onClick={handleManualSync} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Manual Sync
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-20">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading logs...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {logs && logs.length > 0 ? (
              logs.map((log) => (
                <Card key={log.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center gap-3">
                          {log.status === "success" && (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          )}
                          {log.status === "error" && (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                          {log.status === "running" && (
                            <Clock className="h-5 w-5 text-yellow-500 animate-pulse" />
                          )}
                          Sync Run
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <Badge
                        variant={
                          log.status === "success"
                            ? "default"
                            : log.status === "error"
                            ? "destructive"
                            : "secondary"
                        }
                      >
                        {log.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Ideas Added:</span>
                        <Badge variant="outline">{log.ideas_added}</Badge>
                      </div>

                      {log.source_breakdown && Object.keys(log.source_breakdown).length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Source Breakdown:</p>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(log.source_breakdown as Record<string, number>).map(
                              ([source, count]) => (
                                <div
                                  key={source}
                                  className="flex justify-between items-center bg-muted p-2 rounded"
                                >
                                  <span className="text-xs">{source}</span>
                                  <Badge variant="secondary" className="text-xs">
                                    {count}
                                  </Badge>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {log.error_message && (
                        <div className="bg-destructive/10 p-3 rounded">
                          <p className="text-sm font-medium text-destructive mb-1">Error:</p>
                          <p className="text-xs text-muted-foreground">{log.error_message}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="text-center py-20">
                <p className="text-muted-foreground">No automation logs yet</p>
                <Button onClick={handleManualSync} className="mt-4">
                  Run First Sync
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationLogs;
